/** 设置（只读，屏规格 `docs/design/screens/settings.md`，#4598）。
 *
 * 撮合假设 `GET /config/simulation`、验证判据 `GET /config/validation`、风险上限 `GET /risk/limits`，
 * 页头口径指纹与壳层顶栏同源（`GET /status.config_digest`）；口径一致性读 `GET /runs`。
 * 库表版本与「模型与迁移是否一致」读 `GET /store/status` 的 `schema`（#5012）；库表迁移本身只有命令，本屏只读展示。
 * 不提供任何修改入口——配置只从仓里的 `config/*.json` 来，改配置走变更流程。
 * 唯一的输入是「写接口令牌（本机）」：只存本机浏览器，不是服务端配置（#5004）。
 */

import { useStoreStatus } from "../../api/g4";
import { useRuns, useStatus } from "../../api/queries";
import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { digestGroups } from "../../lib/g1";
import { useRiskLimitsRaw, useSimulationConfig, useValidationConfig } from "../../lib/g1Queries";
import { schemaVerdict } from "../../lib/storeSchema";
import { Section } from "../g4/Section";
import { Load } from "../overview/parts";
import { TokenCard } from "./TokenCard";

type Obj = Record<string, unknown>;
const o = (v: unknown): Obj => (typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Obj) : {});
const show = (v: unknown): string => (v === undefined || v === null ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

export function SettingsScreen(): React.ReactNode {
  const status = useStatus();
  const sim = useSimulationConfig();
  const val = useValidationConfig();
  const risk = useRiskLimitsRaw();
  const runs = useRuns();
  const store = useStoreStatus();
  const current = status.data?.config_digest ?? null;
  return (
    <>
      <PageHead title="设置" sub={<>口径指纹 {current ?? "—"} · {status.data?.currency ?? ""} · 起始资金 {show(status.data?.starting_cash)}</>} actions={<Pill tone="neutral">只读</Pill>} />
      <Lede>把撮合假设、验证判据、风险上限三份配置与当前口径指纹摆出来，核对「现在跑的是哪一份口径」；库表版本只读展示。本屏没有服务端配置的修改入口；写接口令牌只存本机浏览器。</Lede>
      <div style={{ marginBottom: 12 }}>
        <TokenCard />
      </div>
      <div className="grid g2">
        <Card title="撮合假设" sub="GET /config/simulation">
          <Load q={sim} what="撮合假设">
            {(data) => {
              const b = data.body;
              const fee = o(b["fee"]);
              return (
                <>
                  {!data.matches_loaded || (current && data.digest !== current) ? (
                    <Note tone="bad">服务端加载的配置（{data.loaded_digest}）与磁盘文件（{data.digest}）不一致，需重启或核查</Note>
                  ) : null}
                  <dl className="kv">
                    <dt>文件</dt>
                    <dd>{data.path}</dd>
                    <dt>指纹</dt>
                    <dd>{data.digest}</dd>
                    <dt>场所 / 币种</dt>
                    <dd>
                      {show(b["venue"])} / {show(b["base_currency"])}
                    </dd>
                    <dt>起始资金</dt>
                    <dd>{show(b["starting_balance"])}</dd>
                    <dt>账户 / 持仓模式</dt>
                    <dd>
                      {show(b["account_type"])} / {show(b["oms_type"])}
                    </dd>
                    <dt>手续费</dt>
                    <dd>
                      {show(fee["model"])}：每股 {show(fee["per_share"])}，最低 {show(fee["min_per_order"])}，上限成交额 {show(fee["max_pct_of_value"])}
                    </dd>
                    <dt>滑点</dt>
                    <dd>{show(o(b["slippage"])["bps"])} bps</dd>
                    <dt>tick</dt>
                    <dd>{show(b["tick_bps"])} bps</dd>
                    <dt>随机种子</dt>
                    <dd>{show(b["random_seed"])}</dd>
                    <dt>标的</dt>
                    <dd>{Array.isArray(b["instruments"]) ? (b["instruments"] as Obj[]).map((i) => `${show(i["symbol"])}.${show(i["venue"])}`).join("、") : "—"}</dd>
                    <dt>执行时点</dt>
                    <dd>
                      {data.execution.signal_at}出信号 → {data.execution.fill_at}撮合；当天成交：{data.execution.same_day_fill ? "是" : "否"}
                    </dd>
                  </dl>
                  <Note>回测 / 纸上盘 / 实盘三处共用这一份；费率目前是全局一套（按标的 / 按券商分配置另开清理单）。</Note>
                </>
              );
            }}
          </Load>
        </Card>
        <Card title="验证判据" sub="GET /config/validation">
          <Load q={val} what="验证判据">
            {(data) => {
              const b = data.body;
              const obs = o(b["observation"]);
              const c = o(b["criteria"]);
              const rf = o(c["risk_free"]);
              const strategies = o(b["strategies"]);
              return (
                <>
                  <dl className="kv">
                    <dt>文件 / 指纹</dt>
                    <dd>
                      {data.path} · {data.digest}
                    </dd>
                    <dt>观察期</dt>
                    <dd>
                      {show(obs["months"])} 个月 · 改参数重置：{obs["reset_on_param_change"] ? "是" : "否"}
                    </dd>
                    <dt>净盈亏 &gt;</dt>
                    <dd>{show(c["net_pnl_cash_gt"])}</dd>
                    <dt>年化 &gt; 无风险</dt>
                    <dd>
                      {c["annualized_return_gt_risk_free"] ? "是" : "否"}（{show(rf["source"])} {show(rf["annual_rate"])}，as_of {show(rf["as_of"])}）
                    </dd>
                    <dt>最大回撤 ≤</dt>
                    <dd>{show(c["max_drawdown_le"])}</dd>
                    <dt>夏普 ≥</dt>
                    <dd>{show(c["sharpe_ge"])}</dd>
                    <dt>未解决对账不平 ≤</dt>
                    <dd>{show(c["unresolved_reconciliation_breaks_le"])}</dd>
                    <dt>未放行停机 ≤</dt>
                    <dd>{show(c["halts_without_recovery_le"])}</dd>
                    <dt>滚动窗口</dt>
                    <dd>{show(b["rolling"])}</dd>
                    <dt>复评周期</dt>
                    <dd>{show(b["review_period_days"])} 天</dd>
                  </dl>
                  <Table
                    cols={[{ label: "策略" }, { label: "失效条件", wrap: true }]}
                    rows={Object.entries(strategies).map(([sid, v]) => ({
                      key: sid,
                      cells: [sid, Array.isArray(o(v)["invalidation"]) ? (o(v)["invalidation"] as Obj[]).map((x) => `${show(x["metric"])} ${x["lt"] !== undefined ? `< ${show(x["lt"])}` : x["gt"] !== undefined ? `> ${show(x["gt"])}` : ""} → ${show(x["action"])}${x["factor"] !== undefined ? ` ×${show(x["factor"])}` : ""}`).join("；") : "—"],
                    }))}
                  />
                  {data.missing_invalidation.length ? (
                    <Note tone="warn">该策略未配置失效条件：{data.missing_invalidation.join("、")}</Note>
                  ) : null}
                </>
              );
            }}
          </Load>
        </Card>
      </div>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="风险上限" sub="GET /risk/limits">
          <Load q={risk} what="风险上限">
            {(data) => {
              const l = data.limits;
              const ovr = o(l["override"]);
              return (
                <>
                  <dl className="kv">
                    <dt>文件 / 指纹</dt>
                    <dd>
                      {data.path} · {data.digest}
                    </dd>
                    <dt>标的层</dt>
                    <dd>{show(l["instrument"])}</dd>
                    <dt>策略层</dt>
                    <dd>{show(l["strategy"])}</dd>
                    <dt>组合层</dt>
                    <dd>{show(l["portfolio"])}</dd>
                    <dt>放行有效期上限</dt>
                    <dd>{ovr["max_ttl_hours"] === undefined ? "未配置放行有效期上限" : `${show(ovr["max_ttl_hours"])} 小时`}</dd>
                  </dl>
                  <Note>纸上盘与实盘读同一份文件；占用与越界在「三层限额」屏。</Note>
                </>
              );
            }}
          </Load>
        </Card>
        <Card title="口径一致性" sub="GET /runs · 各运行的 config_digest_canonical" nopad>
          <Load q={runs} what="运行索引">
            {(list) => {
              const recent = list.slice(-50);
              const groups = digestGroups(recent, current, false);
              const differ = groups.filter((g) => g.consistent === false).reduce((n, g) => n + g.count, 0);
              const legacy = groups.some((g) => g.recorded.some((d) => d !== g.digest));
              return recent.length === 0 ? (
                <Note>还没有运行记录可比对</Note>
              ) : (
                <>
                  <Table
                    cols={[{ label: "指纹" }, { label: "运行数（最近 50 次）", num: true }, { label: "与当前" }]}
                    rows={groups.map((g) => ({
                      key: g.digest,
                      cls: g.consistent === false ? "warn" : "",
                      cells: [g.digest, g.count, g.consistent ? <Pill tone="ok">一致</Pill> : <Pill tone="warn">不一致</Pill>],
                    }))}
                  />
                  {legacy ? <Note>含 2026-10-09 前按旧算法（含起始资金）归档的指纹，已由服务端换算到现行算法后比对。</Note> : null}
                  {differ ? <Note tone="warn">最近 {recent.length} 次运行里有 {differ} 次与当前指纹不一致。</Note> : null}
                </>
              );
            }}
          </Load>
        </Card>
      </div>
      <div style={{ marginTop: 12 }}>
        <Card title="库表版本" sub="GET /store/status · schema · 迁移只走命令 python -m someo_keel.db.schema">
          <Section query={store} what="库表版本" path="/store/status">
            {(data) => {
              if (data.schema === null) return <Note tone="warn">后端未返回库表版本（服务版本早于 #5012）</Note>;
              const v = schemaVerdict(data.schema);
              return (
                <>
                  <dl className="kv" data-testid="store-schema">
                    <dt>库表版本</dt>
                    <dd data-field="schema-version">{v.version}</dd>
                    <dt>迁移文件最新</dt>
                    <dd>{data.schema.latest ?? "—"}</dd>
                    <dt>库与迁移文件</dt>
                    <dd data-field="schema-up-to-date">
                      <Pill tone={v.migration.tone}>{data.schema.up_to_date ? "已跟上" : "落后"}</Pill> {v.migration.text}
                    </dd>
                    <dt>模型与迁移是否一致</dt>
                    <dd data-field="models-match-migrations">
                      <Pill tone={v.models.tone}>{data.schema.models_match_migrations === null ? "判不了" : data.schema.models_match_migrations ? "一致" : "不一致"}</Pill> {v.models.text}
                    </dd>
                  </dl>
                  <Note>升级库表是部署动作（make db-migrate），不在界面上做；不一致时先生成并提交迁移文件（make db-makemigrations）。</Note>
                </>
              );
            }}
          </Section>
        </Card>
      </div>
    </>
  );
}
